(() => {
  if (window.WENNEBIRD_PRODUCT_DELIVERY_LOADED) return;
  window.WENNEBIRD_PRODUCT_DELIVERY_LOADED = true;

  const panels = new Map();
  const catalogPromises = new Map();
  const KNOWN_STATUSES = new Set(['us_stock', 'made_to_order', 'us_stock_out_of_stock']);

  function loadCatalog(url) {
    if (!url) return Promise.resolve({ ok: false, overrides: {} });
    if (!catalogPromises.has(url)) {
      catalogPromises.set(
        url,
        fetch(url, { credentials: 'same-origin', cache: 'no-store' })
          .then((response) => {
            if (!response.ok) throw new Error(`Delivery audit catalog HTTP ${response.status}`);
            return response.json();
          })
          .then((data) => ({
            ok: true,
            overrides: data && typeof data.overrides === 'object' ? data.overrides : {},
          }))
          .catch((error) => {
            console.warn('WENNEBIRD delivery audit catalog unavailable; checkout remains fail-closed.', error);
            return { ok: false, overrides: {} };
          })
      );
    }
    return catalogPromises.get(url);
  }

  function init(root) {
    const panel = root.querySelector('[data-wb-product-delivery]');
    if (!panel || panel.dataset.initialized) return;
    panel.dataset.initialized = 'true';

    let states = {};
    let overrides = {};
    try {
      states = JSON.parse(panel.querySelector('[data-delivery-variants]')?.textContent || '{}');
    } catch {}

    panel.dataset.auditPending = 'true';
    panel.dataset.auditFailed = 'false';
    root.dataset.wbDeliveryAuditPending = 'true';
    root.dataset.wbDeliveryAuditFailed = 'false';

    function render() {
      const id = root.querySelector('.product-variant-id')?.value;
      const baseStatus = id ? states[id] : null;
      const baseKnown = KNOWN_STATUSES.has(baseStatus);
      const audited = !baseKnown && Boolean(id && Object.prototype.hasOwnProperty.call(overrides, id));
      const status = (baseKnown ? baseStatus : (audited ? overrides[id] : baseStatus)) || 'fulfillment_unconfirmed';
      const previousStatus = root.dataset.wbDeliveryStatus;

      panel.dataset.status = status;
      panel.dataset.statusSource = baseKnown ? 'logistics_status' : (audited ? 'r3_inventory_audit_fallback' : 'unconfirmed');
      root.dataset.wbDeliveryStatus = status;
      root.dataset.wbDeliveryVariantId = String(id || '');

      const productForm = root.querySelector('product-form');
      const submitButton = root.querySelector('.product-form__submit');
      const variantInput = root.querySelector('.product-variant-id');
      const dynamicCheckout = root.querySelector('[data-wb-dynamic-checkout]');
      const blocked = status === 'fulfillment_unconfirmed' || status === 'us_stock_out_of_stock';
      const auditPending = !baseKnown && root.dataset.wbDeliveryAuditPending === 'true';
      const auditFailed = !baseKnown && root.dataset.wbDeliveryAuditFailed === 'true';
      const failClosed = !baseKnown && (auditPending || auditFailed);
      const shouldBlock = blocked || failClosed;

      if (!root._wbFitmentInitialized) {
        let available = false;
        try { available = JSON.parse(root.querySelector('[data-wb-product-variants]')?.textContent || '[]').some((variant) => String(variant.id) === String(id) && variant.available); } catch {}
        const guarded = shouldBlock || !available || root.dataset.wbFitmentInvalid === 'true' || root.dataset.wbVariantPending === 'true' || root.dataset.wbVariantError === 'true';
        if (variantInput) variantInput.disabled = guarded;
        if (submitButton) productForm?.toggleSubmitButton?.(guarded, window.variantStrings?.addToCart || 'Add to Cart');
        if (dynamicCheckout) {
          dynamicCheckout.hidden = guarded || status !== 'us_stock';
          dynamicCheckout.setAttribute('aria-hidden', String(dynamicCheckout.hidden));
        }
        root.querySelectorAll('[data-wb-prime-checkout]').forEach((block) => {
          block.hidden = guarded || status !== 'us_stock';
          block.inert = block.hidden;
          block.setAttribute('aria-hidden', String(block.hidden));
        });
      }

      const usNote = panel.querySelector('[data-delivery-us]');
      if (usNote) usNote.hidden = status !== 'us_stock';

      const mto = panel.querySelector('[data-delivery-mto]');
      if (mto) mto.hidden = status !== 'made_to_order';

      const usOos = panel.querySelector('[data-delivery-us-oos]');
      if (usOos) usOos.hidden = status !== 'us_stock_out_of_stock';

      const unknown = panel.querySelector('[data-delivery-unknown]');
      if (unknown) unknown.hidden = status !== 'fulfillment_unconfirmed';

      root.dispatchEvent(new CustomEvent('wb:delivery-status-updated', {
        bubbles: true,
        detail: {
          status,
          audited,
          changed: previousStatus !== status,
          auditPending,
          auditFailed,
        },
      }));
    }

    root.addEventListener('change', () => setTimeout(render, 0));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== String(root.dataset.originalSection || root.dataset.section)) return;
        const updated = data.html?.querySelector('[data-delivery-variants]');
        if (updated) {
          try { states = JSON.parse(updated.textContent); } catch {}
        }
        render();
      });
      panels.set(root, unsubscribe);
    }

    render();
    loadCatalog(panel.dataset.overridesUrl).then((result) => {
      if (!root.isConnected) return;
      overrides = result.overrides;
      panel.dataset.auditPending = 'false';
      panel.dataset.auditFailed = String(!result.ok);
      root.dataset.wbDeliveryAuditPending = 'false';
      root.dataset.wbDeliveryAuditFailed = String(!result.ok);
      render();
    });
  }

  function initAll(scope = document) {
    for (const [root, unsubscribe] of panels) {
      if (!root.isConnected) {
        unsubscribe?.();
        panels.delete(root);
      }
    }
    if (scope.matches?.('product-info')) init(scope);
    scope.querySelectorAll('product-info').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  new MutationObserver((records) => {
    if (
      records.some((record) => Array.from(record.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches?.('product-info') || node.querySelector?.('product-info'))
      )) ||
      Array.from(panels.keys()).some((root) => !root.isConnected)
    ) initAll();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

